import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { useEffect, useState } from 'react';
import { desktop, type PrinterInfo } from './desktop';

GlobalWorkerOptions.workerSrc = workerUrl;

export interface PrintProfile {
  printer: string;
  /** Receipt printer for counter slips. */
  slipPrinter: string;
  silent: boolean;
  paper: 'A4' | 'A5';
  /** Letterhead calibration, in millimetres: shifts what is printed so it lines up with the paper. */
  offsetXmm: number;
  offsetYmm: number;
  copies: number;
}

const KEY = 'qr.print';
export const DEFAULT_PROFILE: PrintProfile = {
  printer: '',
  slipPrinter: '',
  silent: false,
  paper: 'A4',
  offsetXmm: 0,
  offsetYmm: 0,
  copies: 1,
};

export function loadProfile(): PrintProfile {
  try {
    return { ...DEFAULT_PROFILE, ...JSON.parse(localStorage.getItem(KEY) ?? '{}') };
  } catch {
    return DEFAULT_PROFILE;
  }
}

export function saveProfile(p: PrintProfile) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    // storage blocked: the settings simply do not stick
  }
}

const PAGE_MM = { A4: [210, 297], A5: [148, 210] } as const;

/** Pages as images on paper-sized sheets. Offsets move the image to match a pre-printed letterhead. */
export function pagesHtml(
  images: string[],
  p: Pick<PrintProfile, 'paper' | 'offsetXmm' | 'offsetYmm'>,
): string {
  const [w, h] = PAGE_MM[p.paper];
  const sheets = images.map((src) => `<div class="s"><img src="${src}" alt=""></div>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><style>
@page{size:${w}mm ${h}mm;margin:0}*{box-sizing:border-box}body{margin:0}
.s{width:${w}mm;height:${h}mm;overflow:hidden;page-break-after:always;position:relative}
.s:last-child{page-break-after:auto}
img{position:absolute;left:${p.offsetXmm}mm;top:${p.offsetYmm}mm;width:${w}mm;height:${h}mm}
</style></head><body>${sheets}</body></html>`;
}

/** A sheet with a 10 mm grid and labelled marks 10 mm from each edge, to measure printer offsets. */
export function calibrationHtml(
  p: Pick<PrintProfile, 'paper' | 'offsetXmm' | 'offsetYmm'>,
): string {
  const [w, h] = PAGE_MM[p.paper];
  const lines: string[] = [];
  for (let x = 10; x < w; x += 10)
    lines.push(
      `<i style="left:${x}mm;top:0;width:0;height:${h}mm;border-left:.2mm solid ${x % 50 === 0 ? '#000' : '#bbb'}"></i>`,
    );
  for (let y = 10; y < h; y += 10)
    lines.push(
      `<i style="top:${y}mm;left:0;height:0;width:${w}mm;border-top:.2mm solid ${y % 50 === 0 ? '#000' : '#bbb'}"></i>`,
    );
  return `<!doctype html><html><head><meta charset="utf-8"><style>
@page{size:${w}mm ${h}mm;margin:0}body{margin:0;font:3mm sans-serif}
.p{position:relative;width:${w}mm;height:${h}mm;overflow:hidden;transform:translate(${p.offsetXmm}mm,${p.offsetYmm}mm)}
i{position:absolute;display:block}.t{position:absolute;left:14mm;top:14mm;font-size:4mm}
</style></head><body><div class="p">${lines.join('')}
<div class="t"><b>LabView printer test</b><br>${p.paper} · offset ${p.offsetXmm} mm, ${p.offsetYmm} mm<br>
Lines are 10 mm apart. Measure the distance from the paper edge to the first dark line: it should be 50 mm to the dark line.</div></div></body></html>`;
}

/** Draw each PDF page to an image at about 300 dpi. */
export async function pdfToImages(pdf: Uint8Array): Promise<string[]> {
  const doc = await getDocument({ data: pdf.slice() }).promise;
  const out: string[] = [];
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const base = page.getViewport({ scale: 1 });
    // A PDF point is 1/72 inch; 300 dpi is 300/72.
    const viewport = page.getViewport({ scale: 300 / 72 });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    void base;
    await page.render({ canvas, viewport }).promise;
    out.push(canvas.toDataURL('image/png'));
  }
  return out;
}

export async function printPdf(
  pdf: Uint8Array,
  profile: PrintProfile,
): Promise<{ ok: boolean; error?: string }> {
  return printPdfs([pdf], profile);
}

/** Several reports in one print job, one after the other. */
export async function printPdfs(
  pdfs: Uint8Array[],
  profile: PrintProfile,
): Promise<{ ok: boolean; error?: string }> {
  const d = desktop();
  if (!d) return { ok: false, error: 'Printing from the desktop app only' };
  const images: string[] = [];
  for (const pdf of pdfs) images.push(...(await pdfToImages(pdf)));
  const html = pagesHtml(images, profile);
  return d.print.html(html, {
    ...(profile.printer ? { deviceName: profile.printer } : {}),
    silent: profile.silent,
    paper: profile.paper,
    copies: profile.copies,
  });
}

export function usePrinters(): PrinterInfo[] {
  const [list, setList] = useState<PrinterInfo[]>([]);
  useEffect(() => {
    void desktop()
      ?.printers.list()
      .then(setList)
      .catch(() => undefined);
  }, []);
  return list;
}

export async function printSlipHtml(
  html: string,
  paper: 'thermal80' | 'a5',
  profile: PrintProfile,
) {
  const d = desktop();
  if (!d) return { ok: false, error: 'Printing from the desktop app only' };
  return d.print.html(html, {
    ...(profile.slipPrinter ? { deviceName: profile.slipPrinter } : {}),
    silent: profile.silent,
    paper: paper === 'a5' ? 'A5' : 'thermal80',
    heightMm: 200,
  });
}
